import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Chip,
  Button
} from '@mui/material';
import {
  ArrowBack,
  OpenInNew,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  Visibility,
  Star,
  LocalMovies,
  AttachMoney,
  People,
  CalendarToday
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { brandColors } from '../theme/theme';
import legacyQuorumService from '../lib/services/legacyQuorumService';
import { useFilmSubscription } from '../hooks/useFilmSubscription';
import { FILM_TIERS } from '../lib/filmSubscriptionTiers';
import BlurredContent from '../components/Subscription/BlurredContent';

const LegacyQuorumFilmDetail = () => {
  const { filmId } = useParams();
  const navigate = useNavigate();
  const { hasFilmTier, forceUnlocked } = useFilmSubscription();
  const isProfessionalAccess = forceUnlocked || hasFilmTier(FILM_TIERS.PROFESSIONAL);
  
  const [activeTab, setActiveTab] = useState(0);
  const [filmData, setFilmData] = useState(null);
  const [imdbData, setImdbData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchFilmData();
  }, [filmId]);

  const fetchFilmData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch film details with surveys and demographics
      const filmResponse = await legacyQuorumService.getFilmDetails(filmId);
      
      if (!filmResponse.success) {
        throw new Error('Failed to fetch film data');
      }

      setFilmData(filmResponse.data);

      // Fetch IMDB data if available
      if (filmResponse.data.film.imdb_id) {
        const imdbResponse = await legacyQuorumService.getIMDBMetadata(filmResponse.data.film.imdb_id);
        if (imdbResponse.success && imdbResponse.data) {
          setImdbData(imdbResponse.data);
        } else {
          console.warn('IMDB data not available for:', filmResponse.data.film.imdb_id);
        }
      }

    } catch (err) {
      console.error('Error fetching film data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return `${value.toFixed(1)}%`;
  };

  const getTrendIcon = (value) => {
    if (value > 0) return <TrendingUp sx={{ fontSize: '1rem' }} />;
    if (value < 0) return <TrendingDown sx={{ fontSize: '1rem' }} />;
    return <TrendingFlat sx={{ fontSize: '1rem' }} />;
  };

  const getTrendColor = (value) => {
    if (value > 0) return 'success.main';
    if (value < 0) return 'error.main';
    return 'text.secondary';
  };

  // Transform survey data for charts
  const transformSurveyData = (surveys) => {
    if (!surveys) return [];
    
    return surveys.map(survey => ({
      date: formatDate(survey.survey_date?.value || survey.date_survey_id),
      awareness: survey.awareness,
      interest: survey.interest,
      location: survey.location,
      paytosee: survey.paytosee
    })).reverse(); // Show chronological order
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ color: brandColors.primary, mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Loading film details...
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Error Loading Film Details
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/film-tracking/legacy-quorum')}
        >
          Back to Legacy Quorum
        </Button>
      </Container>
    );
  }

  if (!filmData) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="warning">
          No film data available
        </Alert>
      </Container>
    );
  }

  const film = filmData.film;
  const surveys = filmData.surveys || [];
  const surveyChartData = transformSurveyData(surveys);

  const requireProfessional = (content, featureName = 'Legacy Quorum Analytics', blurAmount = 12) => {
    if (isProfessionalAccess) {
      return content;
    }
    return (
      <BlurredContent requiredTier={FILM_TIERS.PROFESSIONAL} featureName={featureName} blurAmount={blurAmount}>
        {content}
      </BlurredContent>
    );
  };
 
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton
            onClick={() => navigate('/film-tracking/legacy-quorum')}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
              {film.film_name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" color="text.secondary">
                {film.release_year || 'Unknown Year'}
              </Typography>
              <Chip 
                label="Legacy Quorum" 
                size="small" 
                sx={{ 
                  backgroundColor: brandColors.secondary + '20',
                  color: brandColors.secondary,
                  fontWeight: 600
                }} 
              />
              {film.imdb_id && (
                <Button
                  size="small"
                  variant="outlined"
                  endIcon={<OpenInNew />}
                  onClick={() => window.open(`https://www.imdb.com/title/${film.imdb_id}`, '_blank')}
                >
                  View on IMDB
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {!isProfessionalAccess && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Data is presented in preview mode. Upgrade to Film Professional to unlock full Legacy Quorum details.
          </Alert>
        )}

        {/* Key Metrics Overview */}
        {requireProfessional(
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                border: `2px solid ${brandColors.awareness}20`,
                backgroundColor: `${brandColors.awareness}05`
              }}>
                <CardContent sx={{ textAlign: 'center', p: 2 }}>
                  <Visibility sx={{ fontSize: '2rem', color: brandColors.awareness, mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 700, color: brandColors.awareness, mb: 0.5 }}>
                    {formatPercentage(film.latest_awareness)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Latest Awareness
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Avg: {formatPercentage(film.avg_awareness)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                border: `2px solid ${brandColors.highInterest}20`,
                backgroundColor: `${brandColors.highInterest}05`
              }}>
                <CardContent sx={{ textAlign: 'center', p: 2 }}>
                  <Star sx={{ fontSize: '2rem', color: brandColors.highInterest, mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 700, color: brandColors.highInterest, mb: 0.5 }}>
                    {formatPercentage(film.latest_interest)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Latest Interest
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Avg: {formatPercentage(film.avg_interest)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                border: `2px solid ${brandColors.theaterIntent}20`,
                backgroundColor: `${brandColors.theaterIntent}05`
              }}>
                <CardContent sx={{ textAlign: 'center', p: 2 }}>
                  <LocalMovies sx={{ fontSize: '2rem', color: brandColors.theaterIntent, mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 700, color: brandColors.theaterIntent, mb: 0.5 }}>
                    {formatPercentage(film.latest_location)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Theater Intent
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Avg: {formatPercentage(film.avg_location)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                border: `2px solid ${brandColors.willingToPay}20`,
                backgroundColor: `${brandColors.willingToPay}05`
              }}>
                <CardContent sx={{ textAlign: 'center', p: 2 }}>
                  <AttachMoney sx={{ fontSize: '2rem', color: brandColors.willingToPay, mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 700, color: brandColors.willingToPay, mb: 0.5 }}>
                    {formatPercentage(film.latest_paytosee)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Willing to Pay
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Avg: {formatPercentage(film.avg_paytosee)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Tab Navigation */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} centered>
          <Tab label="Survey Analytics" />
          <Tab label="Film Information" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Box>
          {/* Survey Timeline Chart */}
          {requireProfessional(
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Survey Trends Over Time
                </Typography>
                <Box sx={{ height: 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={surveyChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis domain={[0, 100]} />
                      <RechartsTooltip />
                      <Line 
                        type="monotone" 
                        dataKey="awareness" 
                        stroke={brandColors.awareness} 
                        strokeWidth={3}
                        name="Awareness"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="interest" 
                        stroke={brandColors.highInterest} 
                        strokeWidth={3}
                        name="Interest"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="location" 
                        stroke={brandColors.theaterIntent} 
                        strokeWidth={3}
                        name="Theater Intent"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="paytosee" 
                        stroke={brandColors.willingToPay} 
                        strokeWidth={3}
                        name="Willing to Pay"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Survey Statistics */}
          {requireProfessional(
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Survey Overview
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <People sx={{ fontSize: '1.5rem', mr: 1, color: brandColors.primary }} />
                      <Typography variant="body1">
                        <strong>{film.survey_count}</strong> surveys conducted
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <CalendarToday sx={{ fontSize: '1.5rem', mr: 1, color: brandColors.primary }} />
                      <Typography variant="body1">
                        From {formatDate(film.first_survey_date)} to {formatDate(film.last_survey_date)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Volatility Analysis
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ color: getTrendColor(film.awareness_volatility), mr: 1 }}>
                        {getTrendIcon(film.awareness_volatility)}
                      </Box>
                      <Typography variant="body1">
                        Awareness Volatility: <strong>{film.awareness_volatility?.toFixed(1)}%</strong>
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ color: getTrendColor(film.interest_volatility), mr: 1 }}>
                        {getTrendIcon(film.interest_volatility)}
                      </Box>
                      <Typography variant="body1">
                        Interest Volatility: <strong>{film.interest_volatility?.toFixed(1)}%</strong>
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Box>
      )}

      {activeTab === 1 && (
        requireProfessional(
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Film Information
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Title:</strong> {imdbData?.movie?.title_text || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Release Year:</strong> {imdbData?.movie?.release_year || film.release_year || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Genres:</strong> {imdbData?.movie?.genres?.join(', ') || film.genre || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Runtime:</strong> {imdbData?.movie?.runtime_minutes ? `${imdbData.movie.runtime_minutes} minutes` : 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Rating:</strong> {imdbData?.movie?.ratings?.aggregate_rating || 'N/A'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Box Office Insights
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Production Budget
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {imdbData?.movie?.ratings?.box_office?.production_budget?.amount
                          ? `$${imdbData.movie.ratings.box_office.production_budget.amount.toLocaleString()}`
                          : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Domestic Lifetime
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {imdbData?.movie?.ratings?.box_office?.domestic_lifetime?.total_amount
                          ? `$${imdbData.movie.ratings.box_office.domestic_lifetime.total_amount.toLocaleString()}`
                          : 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )
      )}
    </Container>
  );
};

export default LegacyQuorumFilmDetail;
